// M4 workstream B (markets & money) — tick hooks called by src/sim/m4/index.js. Inert before 1 Jan 2000.
//   tickPre  (before drilling & production): month WTI average, zone output, shut-in restarts, negative-day notice,
//            curtailments (gas-capture rules, Alberta 2019-20), firm-pipeline commitments.
//   tickPost (after production & shale): gas/NGL sales, flaring, methane & carbon, 45Q, negative-day accounting,
//            take-or-pay, crude-by-rail derailments, exports, Cushing rent, oil-sands payout.
//   tickEcon (end of day): M4 hedges, RBL redeterminations & deficiencies, capital discipline, land rig board,
//            Pennsylvania impact fee, negative-day stat.
// For workstream A: fracRateIndex(state) and serviceWaitDays(state) price & queue completion services.
import { TAKEAWAY_ZONES, dayRateM4 } from '../../data/m4/rules.js';
import { dailyFromAnnual } from '../../data/m2/rules.js';
import { allWells } from '../indexes.js';
import { dayToDate } from '../../core/time.js';
import { eqDef, fieldDef, stat, eraDollars, yf } from '../defs.js';
import { netback, royaltyRate } from '../production.js';
import { emit } from '../../core/bus.js';
import { spend, notify, fmt$ } from '../util.js';
import { mk, peek, m4On, rand, r2 } from './marketCore.js';
import { fieldZoneId, fieldPriceInfo, zoneCurtailment, zoneSpreadVsWti, ALBERTA_EXEMPT_BBLD } from './marketPrices.js';
import { tickShutIns, tickNegativePre, tickNegativePost, tickNegativeEcon, tickCushing } from './marketNegative.js';
import { tickGas, flaringCurtailShare, applyCurtailments, wellStreamsToday, wellMakesGas } from './marketGas.js';
import { tickWtiAverage, tickHedgesM4, tickM3HedgeGains, tickRbl, tickCapitalDiscipline } from './marketFinance.js';
import { tickLandBoard, fracRateIndexAt, serviceWaitDays as waitDays } from './marketRigs.js';
import { tickImpactFee, notePayout } from './marketFiscal.js';

export const fracRateIndex = (state) => fracRateIndexAt(state);
export const serviceWaitDays = (state) => waitDays(state);
export const DERAIL = { lossShareOfTrain: 0.3, cleanupPerBbl: 300, baseYear: 2014, rep: 3, dot117From: 2015.4, dot117Mult: 0.5 };
export const TAKEAWAY_CRUNCH_SPREAD = -10;

function playerFields(state) {
  const out = new Map();
  for (const w of allWells(state)) {
    if (w.ownerOperatorId || !w.prod || (w.status !== 'PRODUCING' && w.status !== 'SHUT_IN')) continue;
    let F = out.get(w.fieldId);
    if (!F) out.set(w.fieldId, (F = { wells: 0, rate: 0, gasWells: 0 }));
    if (w.status === 'PRODUCING') { F.wells++; F.rate += w.prod.rate ?? 0; }
    if (wellMakesGas(w)) F.gasWells++;
  }
  return out;
}

export function tickPre(state) {
  if (!m4On(state)) return;
  const M = mk(state);
  const y = yf(state);
  tickWtiAverage(state);
  tickShutIns(state);
  tickNegativePre(state);
  const PF = playerFields(state);
  // player crude by takeaway zone (kbd), for the endogenous differential
  const kbd = {};
  for (const [fid, F] of PF) { const z = fieldZoneId(fid); if (z && TAKEAWAY_ZONES[z]?.capacityKbd) kbd[z] = (kbd[z] ?? 0) + F.rate / 1000; }
  M.zoneKbd = Object.fromEntries(Object.entries(kbd).map(([k, v]) => [k, r2(v)]));
  // curtailments
  const inAlberta = (fid) => fieldDef(fid)?.regionId === 'alberta'; // the 2019 order covered crude and bitumen producers
  const albertaBbld = [...PF].filter(([fid]) => inAlberta(fid)).reduce((a, [, F]) => a + F.rate, 0);
  const shares = {};
  for (const [fid, F] of PF) {
    if (!F.wells) continue;
    let share = 0, reason = null, gasOnly = false;
    if (F.gasWells > 0) { const s = flaringCurtailShare(state, fid); if (s > share) { share = s; reason = 'flaring'; gasOnly = true; } }
    if (inAlberta(fid) && albertaBbld > ALBERTA_EXEMPT_BBLD) { const s = zoneCurtailment('alberta', y); if (s > share) { share = s; reason = 'alberta'; gasOnly = false; } }
    if (share > 0) shares[fid] = { share, reason, gasOnly };
  }
  applyCurtailments(state, shares);
  // firm pipelines: a take-or-pay commitment starts when the field first ships on one
  for (const [fid] of PF) {
    const f = state.fields[fid];
    const d = f?.transportId ? eqDef(f.transportId) : null;
    if (!d || !stat(d, 'takeOrPay', false) || d.stats.product === 'lng' || d.stats.product === 'natural_gas') continue;
    const cur = M.firm[fid];
    if (cur && cur.defId === d.id && state.day < cur.untilDay) continue;
    if (cur && state.day < cur.untilDay) continue; // the old commitment keeps running (take-or-pay)
    M.firm[fid] = { defId: d.id, bbld: Math.max(100, Math.round(PF.get(fid).rate)), startDay: state.day, untilDay: state.day + Math.round(stat(d, 'termYears', 5) * 365), paid: 0 };
  }
}

export function tickPost(state) {
  if (!m4On(state)) return;
  const M = mk(state);
  const y = yf(state);
  tickGas(state);
  tickNegativePost(state);
  // take-or-pay shortfalls on firm crude pipelines
  for (const [fid, C] of Object.entries(M.firm)) {
    if (state.day >= C.untilDay) { delete M.firm[fid]; continue; }
    const f = state.fields[fid];
    const d = eqDef(C.defId);
    const shipped = f?.transportId === C.defId ? (f.shippedToday ?? 0) : 0;
    const short = Math.max(0, C.bbld - shipped);
    if (short > 0 && d) { const amt = short * eraDollars(state, stat(d, 'costPerBbl', 3), d.baseYear ?? d.year); spend(state, amt, 'transport', false); C.paid += amt; M.ledger.takeOrPay += amt; }
  }
  for (const f of Object.values(state.fields)) {
    if (!(f.shippedToday > 0)) continue;
    // crude-by-rail derailments (DOT-117 tank cars halve the risk from 2015)
    if (f.transportId === 'crude_by_rail') {
      const d = eqDef('crude_by_rail');
      const pYear = stat(d, 'derailRiskPerYear', 0.002) * (y >= DERAIL.dot117From ? DERAIL.dot117Mult : 1);
      if (rand(state, `derail:${f.id}`) < dailyFromAnnual(pYear)) {
        const lost = Math.min(f.shippedToday, stat(d, 'capacityPerDay', 70000) * DERAIL.lossShareOfTrain);
        const cost = lost * eraDollars(state, DERAIL.cleanupPerBbl, DERAIL.baseYear) + lost * Math.max(0, fieldPriceInfo(state, f.id).price);
        spend(state, cost, 'spills', false);
        state.company.reputation = Math.max(0, state.company.reputation - DERAIL.rep);
        notify(state, 'news', { fieldId: f.id, kind: 'incident', level: 'major', pause: true, text: `A unit train of ${fieldDef(f.id)?.name} crude derails and burns: ${Math.round(lost).toLocaleString('en-US')} bbl lost, ${fmt$(cost)} in cleanup and claims. Reputation −${DERAIL.rep}.` });
      }
    }
    const info = fieldPriceInfo(state, f.id);
    if (info.kind === 'export') state.company.stats.exportedBbl = (state.company.stats.exportedBbl ?? 0) + f.shippedToday;
    // Alberta oil-sands payout ledger
    if (fieldDef(f.id)?.oilSands) { const nb = netback(state, f.id); notePayout(state, f.id, f.shippedToday * Math.max(0, nb.gross) * (1 - royaltyRate(state, f.id))); }
    // takeaway crunch (front-page trigger for C / news): player field hub spread below −$10
    const z = info.zone;
    if (z && TAKEAWAY_ZONES[z]?.capacityKbd) {
      const spread = zoneSpreadVsWti(state, z);
      M.crunch ??= {};
      if (spread < TAKEAWAY_CRUNCH_SPREAD && !(state.day - (M.crunch[z] ?? -1e9) < 180)) {
        M.crunch[z] = state.day;
        try { emit('takeaway', { zone: z, spread: r2(spread), fieldId: f.id }); } catch (e) { /* UI */ }
        notify(state, 'news', { fieldId: f.id, kind: 'market', level: 'warn', text: `Pipelines out of ${fieldDef(f.id)?.name} are full: local crude trades ${fmt$(-spread)} under Cushing. Rail, firm long-haul capacity or patience.` });
      }
    }
  }
  // Cushing storage: shop-bought tank leases pay the day rate; contango leases roll
  let leaseItems = 0;
  for (const it of Object.values(state.items)) if (it.defId === 'cushing_storage_lease') leaseItems++;
  if (leaseItems > 0) { const r = leaseItems * dayRateM4('cushing_storage', y); spend(state, r, 'storage', false); M.ledger.storageRent += r; }
  tickCushing(state);
}

export function tickEcon(state) {
  if (!m4On(state)) return;
  const { dom, month } = dayToDate(state.day);
  tickHedgesM4(state);
  tickM3HedgeGains(state);
  tickRbl(state, { dom, month });
  tickCapitalDiscipline(state, { dom, month });
  tickLandBoard(state);
  tickImpactFee(state);
  tickNegativeEcon(state);
  const M = peek(state);
  if (M?.reactivating) for (const [uid, v] of Object.entries(M.reactivating)) if (state.day >= v.untilDay) delete M.reactivating[uid];
}
export { wellStreamsToday };
